import {
  Typography,
  CardMedia,
  useTheme,
  Box,
  Divider,
} from "@mui/material";
import Stack from "@mui/material/Stack";

import React, { useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";

import { useDispatch } from "react-redux";
import {
  setBookmarkedCollectionIds,
  setCollectionId,
  setCollectionSourceId,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";
import { logEvent } from "services/amplitude.service";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export const WikiCard = ({ data, cardRef, category, query }) => {
  const [isHovered, setIsHovered] = useState(false);

  const theme = useTheme();
  const dispatch = useDispatch();

  const imageUrl = data?.openGraphImage?.contentUrl
    ? data?.openGraphImage?.contentUrl
    : defaultBackground;

  const newUrl = data.url;

  const openSidebar = () => {
    logEvent("open_bookmark_popup", {
      url: newUrl,
      query: query,
      entity: data.entityName,
      type: "wikipedia",
      category: data.url.toLowerCase().includes("merriam")
        ? "Merriam Webster"
        : "Britannica",
      mediabiasrating: data.mediaBiasRating ? data.mediaBiasRating : "NA",
    });
    const collectionIds = (data.collectionMemberships || [])
      .map((membership) => membership.collectionID_lng)
      .filter(Boolean);

    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    setIsHovered(false);
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: data.url.includes("britannica")
          ? "Britannica"
          : "Merriam Webster",
        title: data.name,
        published_date: data.datePublished,
        url: data.url,
        utc_date_published: data.datePublished,
      })
    );
    dispatch(setCollectionId(data.collectionId ?? collectionIds[0]));
    dispatch(
      setBookmarkedCollectionIds(
        collectionIds.length > 0
          ? collectionIds
          : data.collectionId
          ? [data.collectionId]
          : []
      )
    );
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const BookmarkOptions = () => {
    return (
      <Stack
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          cursor: "pointer",
          mt: "auto",
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
          pl: 1,
          pr: 1,
        }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        mt={1}
      >
        {data.collectionSourceId ? (
          <CollectionsLogo
            onClick={openSidebar}
            alt="Collections"
            style={{
              width: 18,
              height: 18,
              fill: "blue",
              color: "blue",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 18,
              height: 18,
              cursor: "pointer",
            }}
          >
            {isHovered && (
              <CollectionsLogoHover
                onClick={openSidebar}
                alt="Collections"
                style={{
                  fill: "blue",
                  color: "blue",
                }}
              />
            )}
          </Box>
        )}
        <AccessibleIconButton
          sx={{
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          title="Open Bookmark"
          onClick={openSidebar}
        >
          <MoreHorizIcon />
        </AccessibleIconButton>
      </Stack>
    );
  };

  const NotesSection = () => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            // height: { xs: "10vh", sm: "6vh" },
          }}
        >
          <Stack>
            <Typography
              variant="body1"
              sx={{
                color: "grey.600",
                fontSize: { xs: "1.4rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: { xs: 3, sm: 4 },
                WebkitBoxOrient: "vertical",
                maxHeight: { xs: "6vh", sm: "8vh" },
                lineHeight: { xs: "2vh", sm: "2vh" },
                height: "8vh",
                width: "98%",
              }}
            >
              {data.datePublishedDisplayText &&
                `${data.datePublishedDisplayText} - `}
              {data.snippet}
            </Typography>
          </Stack>
        </Box>
      </>
    );
  };

  return (
    <React.Fragment>
      <Box
        ref={cardRef}
        sx={{
          display: "grid",
          gridTemplateAreas: `
        'titleSection imageSection'
        'notesSection imageSection'
        'divider divider'
      `,
          gridTemplateColumns: "4fr 1fr",
          gridTemplateRows: "auto 1fr auto",
          gap: 1,
          width: "100%",
          maxHeight: { xs: "unset", sm: "19vh" },
          boxShadow: "none",
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            gridArea: "titleSection",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <SearchResultLink
            url={data.url}
            trackingCat="click_resultset_title"
            trackingOptions={{
              url: newUrl,
              query: query,
              entity: data.entityName,
              type: "wikipedia",
              category: data?.url?.toLowerCase().includes("merriam")
                ? "Merriam Webster"
                : "Britannica",
              mediabiasrating: data.mediaBiasRating
                ? data.mediaBiasRating
                : "NA",
              title: data.name,
            }}
            variant="body4"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              maxHeight: "5em",
              lineHeight: "1.5em",
              maxWidth: "100%",
              flexShrink: 0,
              cursor: "pointer",
              "&:hover": {
                color: theme.palette.primary.light,
              },
            }}
          >
            {data.name}
          </SearchResultLink>
        </Box>

        {/* Notes Section */}
        <Box sx={{ gridArea: "notesSection" }}>
          <NotesSection />
        </Box>

        {/* Image Section */}
        <Box
          sx={{
            gridArea: "imageSection",
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
            pr: 1,
          }}
        >
          <CardMedia
            component="img"
            sx={{ height: "12vh", borderRadius: 1 }}
            image={imageUrl}
            alt="No Image Found"
          />
          <Box sx={{ mt: 1 }}>
            <BookmarkOptions />
          </Box>
        </Box>

        {/* Divider Section */}
        <Box sx={{ gridArea: "divider", mb: 2 }}>
          <Divider />
        </Box>
      </Box>
    </React.Fragment>
  );
};
